<template>
  <div class="campaign-dashboard-page">
    <MenuBar />

    <div class="content">
      <div class="page-header">
        <div>
          <h1>Campaign Dashboard</h1>
          <p>Enhanced view for campaign health, daily sponsor trend, and work status.</p>
        </div>
        <button class="btn-refresh" @click="loadDashboard" :disabled="loading">{{ loading ? 'Loading...' : 'Refresh' }}</button>
      </div>

      <div class="summary-grid">
        <article class="card stat-card running">
          <p class="label">Running Campaigns</p>
          <strong>{{ summary.runningCampaigns }}</strong>
        </article>
        <article class="card stat-card">
          <p class="label">Archived Campaigns</p>
          <strong>{{ summary.archivedCampaigns }}</strong>
        </article>
        <article class="card stat-card">
          <p class="label">Total Campaigns</p>
          <strong>{{ summary.totalCampaigns }}</strong>
        </article>
        <article class="card stat-card progress-card">
          <p class="label">Average Progress</p>
          <strong>{{ averageProgress }}%</strong>
        </article>
      </div>

      <section class="card alert-panel">
        <div class="alert-panel-head">
          <h2>Deadline Risk Alerts</h2>
          <span class="chip">Running Campaigns Only</span>
        </div>
        <div class="alert-counts">
          <article class="alert-card alert-critical">
            <span class="alert-icon">🔴</span>
            <div>
              <strong>{{ riskCounts.critical }}</strong>
              <p>Critical</p>
            </div>
          </article>
          <article class="alert-card alert-at-risk">
            <span class="alert-icon">🟠</span>
            <div>
              <strong>{{ riskCounts.at_risk }}</strong>
              <p>At Risk</p>
            </div>
          </article>
          <article class="alert-card alert-warning">
            <span class="alert-icon">🟡</span>
            <div>
              <strong>{{ riskCounts.warning }}</strong>
              <p>Warning</p>
            </div>
          </article>
          <article class="alert-card alert-on-track">
            <span class="alert-icon">✅</span>
            <div>
              <strong>{{ riskCounts.on_track }}</strong>
              <p>On Track</p>
            </div>
          </article>
        </div>
        <div class="alert-legend">
          <p class="legend-title">Alert Level Guide</p>
          <ul class="legend-list">
            <li><span class="risk-badge risk-critical">Critical</span> — Deadline has already passed with content still remaining, OR the campaign is past 50% of its duration with zero submissions.</li>
            <li><span class="risk-badge risk-at_risk">At Risk</span> — Required daily upload rate is 50%+ higher than planned, OR 3 or fewer days remain with less than 80% progress.</li>
            <li><span class="risk-badge risk-warning">Warning</span> — Actual progress is 25+ percentage points behind the expected pace, OR 7 or fewer days remain with less than 50% progress.</li>
            <li><span class="risk-badge risk-on_track">On Track</span> — Campaign is progressing at or ahead of schedule.</li>
            <li><span class="risk-badge risk-none">N/A</span> — No content target set, campaign is completed, or archived.</li>
          </ul>
        </div>
      </section>

      <section class="card filter-card">
        <h2>Filters</h2>
        <div class="filters">
          <div class="field">
            <label for="filter-name">Name</label>
            <input id="filter-name" v-model.trim="filters.name" type="text" placeholder="Search campaign name" />
          </div>
          <div class="field">
            <label for="filter-status">Work Status</label>
            <select id="filter-status" v-model="filters.status">
              <option value="">All Status</option>
              <option value="campaign_initiated">Campaign Initiated</option>
              <option value="task_accepted">Task Accepted</option>
              <option value="creative_production">Creative Production</option>
              <option value="creative_approved">Creative Approved</option>
              <option value="publishing">Publishing</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div class="field">
            <label for="filter-archive">Campaign Type</label>
            <select id="filter-archive" v-model="filters.archiveType">
              <option value="all">All</option>
              <option value="running">Running</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div class="field">
            <label for="filter-start">From Date</label>
            <input id="filter-start" v-model="filters.startDate" type="date" />
          </div>
          <div class="field">
            <label for="filter-end">To Date</label>
            <input id="filter-end" v-model="filters.endDate" type="date" />
          </div>
          <div class="actions">
            <button class="btn-clear" @click="resetFilters">Clear</button>
          </div>
        </div>
      </section>

      <section class="card chart-card">
        <div class="section-head">
          <h2>Daily Sponsor Trend</h2>
          <span class="chip">Line Series</span>
        </div>
        <div class="chart-wrap">
          <canvas ref="uploadTrendChart"></canvas>
        </div>
      </section>

      <section class="card table-card">
        <div class="section-head">
          <h2>Campaign Work Status</h2>
          <span class="chip">{{ filteredCampaigns.length }} Rows</span>
        </div>

        <div v-if="loading" class="empty-state">Loading campaigns...</div>
        <div v-else-if="!filteredCampaigns.length" class="empty-state">No campaigns matched your filters.</div>

        <div v-else class="table-wrap">
          <table class="campaign-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Sponsor</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Work Status</th>
                <th>Type</th>
                <th>Target</th>
                <th>Submitted</th>
                <th>Remaining</th>
                <th>Progress</th>
                <th>Risk</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="campaign in filteredCampaigns" :key="campaign.id" :class="riskRowClass(campaign)">
                <td><button class="btn-campaign-name" @click="openContentModal(campaign)">{{ campaign.campaignName || '-' }}</button></td>
                <td>{{ campaign.sponsorName || '-' }}</td>
                <td>{{ humanDate(campaign.startDate) }}</td>
                <td>{{ humanDate(campaign.endDate) }}</td>
                <td><span class="status" :class="`s-${campaign.workStatus}`">{{ statusLabel(campaign.workStatus) }}</span></td>
                <td><span class="type-pill" :class="campaign.isArchived ? 'archived' : 'running'">{{ campaign.isArchived ? 'Archived' : 'Running' }}</span></td>
                <td>{{ Number(campaign.targetPlan && campaign.targetPlan.totalTarget || 0) }}</td>
                <td>{{ Number(campaign.submittedCount || 0) }}</td>
                <td>{{ Number(campaign.remainingCount || 0) }}</td>
                <td><span class="progress-pill">{{ Number(campaign.targetPlan && campaign.targetPlan.progressPercent || 0) }}%</span></td>
                <td>
                  <span
                    v-if="campaign.deadlineRisk && campaign.deadlineRisk.level !== 'none'"
                    class="risk-badge"
                    :class="`risk-${campaign.deadlineRisk.level}`"
                    :title="riskTitle(campaign)"
                  >{{ riskLabel(campaign.deadlineRisk.level) }}</span>
                  <span v-else class="risk-badge risk-none">N/A</span>
                </td>
                <td>
                  <button
                    v-if="!campaign.isArchived"
                    class="btn-work-log"
                    @click="openWorkLog(campaign)"
                  >
                    Work Log
                  </button>
                  <span v-else class="archive-note">Archived</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <div v-if="contentModalCampaign" class="modal-backdrop" @click.self="closeContentModal">
        <div class="content-modal card">
          <div class="modal-head">
            <h3>Campaign Content</h3>
            <button class="btn-close" @click="closeContentModal">Close</button>
          </div>
          <div class="modal-subtitle">{{ contentModalCampaign.campaignName || '-' }} — {{ contentModalCampaign.sponsorName || '-' }}</div>

          <div v-if="!contentUploads.length" class="empty-state" style="margin-top:16px;">No content uploaded for this campaign.</div>
          <div v-else>
            <table class="content-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Title</th>
                  <th>Category</th>
                  <th>YouTube Link</th>
                  <th>Facebook Link</th>
                  <th>Uploaded By</th>
                  <th>Upload Time</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in pagedContentUploads" :key="item.id || idx">
                  <td>{{ (contentPage - 1) * contentPerPage + idx + 1 }}</td>
                  <td>{{ item.title || '-' }}</td>
                  <td>{{ item.category || '-' }}</td>
                  <td>
                    <a v-if="item.youtubeLink" :href="item.youtubeLink" target="_blank" rel="noopener noreferrer" class="content-link">YouTube</a>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <a v-if="item.facebookLink" :href="item.facebookLink" target="_blank" rel="noopener noreferrer" class="content-link">Facebook</a>
                    <span v-else>-</span>
                  </td>
                  <td>{{ item.createdBy || '-' }}</td>
                  <td>{{ workLogDate(item.createdAt || item.uploadedDate) }}</td>
                </tr>
              </tbody>
            </table>
            <div class="content-pagination">
              <span class="pagination-info">Page {{ contentPage }} of {{ contentTotalPages }} ({{ contentUploads.length }} items)</span>
              <div class="pagination-controls">
                <button class="btn-page" :disabled="contentPage <= 1" @click="contentPrevPage">← Prev</button>
                <button class="btn-page" :disabled="contentPage >= contentTotalPages" @click="contentNextPage">Next →</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="selectedCampaign" class="modal-backdrop" @click.self="closeWorkLog">
        <div class="worklog-modal card">
          <div class="modal-head">
            <h3>Campaign Work Log</h3>
            <button class="btn-close" @click="closeWorkLog">Close</button>
          </div>

          <div class="modal-subtitle">{{ selectedCampaign.campaignName || '-' }} - {{ selectedCampaign.sponsorName || '-' }}</div>

          <section class="short-details">
            <h4>Campaign Short Details</h4>
            <div class="details-grid">
              <p><strong>Start:</strong> {{ humanDate(workLogDetails.startDate) }}</p>
              <p><strong>End:</strong> {{ humanDate(workLogDetails.endDate) }}</p>
              <p><strong>Assigned Creative:</strong> {{ workLogDetails.assignedCreative || '-' }}</p>
              <p><strong>Total Target:</strong> {{ Number(workLogDetails.totalTarget || 0) }}</p>
              <p><strong>Submitted:</strong> {{ Number(workLogDetails.submittedCount || 0) }}</p>
              <p><strong>Remaining:</strong> {{ Number(workLogDetails.remainingCount || 0) }}</p>
              <p><strong>Categories:</strong> {{ joinedCategories }}</p>
              <p><strong>Remarks:</strong> {{ workLogDetails.remarks || '-' }}</p>
            </div>
          </section>

          <section class="work-log-list">
            <h4>Status Action Log</h4>
            <div v-if="!workLogs.length" class="empty-state">No status log available.</div>
            <ul v-else>
              <li v-for="(log, idx) in workLogs" :key="`${log.status}-${idx}`">
                <span class="log-time">{{ workLogDate(log.at) }}</span>
                <span class="log-text">Status {{ statusLabel(log.status) }} by {{ actorLabel(log.actor) }}</span>
                <span v-if="log.note" class="log-note">{{ log.note }}</span>
              </li>
            </ul>
          </section>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import Chart from 'chart.js';
import MenuBar from './MenuBar.vue';
import sponsorService from '../services/sponsorService';
import { campaignStatusLabel } from '../utils/campaignLifecycle';

export default {
  name: 'CampaignStatusDashboard',
  components: { MenuBar },
  data() {
    return {
      loading: false,
      message: '',
      campaigns: [],
      summary: {
        totalCampaigns: 0,
        runningCampaigns: 0,
        archivedCampaigns: 0,
      },
      filters: {
        name: '',
        status: '',
        archiveType: 'all',
        startDate: '',
        endDate: '',
      },
      chartInstance: null,
      selectedCampaign: null,
      contentModalCampaign: null,
      contentPage: 1,
      contentPerPage: 10,
    };
  },
  computed: {
    averageProgress() {
      if (!this.filteredCampaigns.length) return 0;
      const total = this.filteredCampaigns.reduce((sum, item) => sum + Number(item.targetPlan && item.targetPlan.progressPercent || 0), 0);
      return Math.round(total / this.filteredCampaigns.length);
    },
    riskCounts() {
      const counts = { critical: 0, at_risk: 0, warning: 0, on_track: 0 };
      this.filteredCampaigns.forEach(c => {
        if (c.isArchived) return;
        const level = c.deadlineRisk && c.deadlineRisk.level;
        if (level && level in counts) counts[level]++;
      });
      return counts;
    },
    filteredCampaigns() {
      return this.campaigns.filter(campaign => {
        const name = String(campaign.campaignName || '').toLowerCase();
        const status = String(campaign.workStatus || '');

        if (this.filters.name && !name.includes(this.filters.name.toLowerCase())) return false;
        if (this.filters.status && status !== this.filters.status) return false;
        if (this.filters.archiveType === 'running' && campaign.isArchived) return false;
        if (this.filters.archiveType === 'archived' && !campaign.isArchived) return false;

        const startMs = this.parseDateMs(campaign.startDate);
        const endMs = this.parseDateMs(campaign.endDate);

        if (this.filters.startDate) {
          const fromMs = this.parseDateMs(this.filters.startDate, true);
          if (fromMs !== null) {
            if (startMs !== null && startMs < fromMs) return false;
            if (startMs === null && endMs !== null && endMs < fromMs) return false;
          }
        }

        if (this.filters.endDate) {
          const toMs = this.parseDateMs(this.filters.endDate, true, true);
          if (toMs !== null) {
            if (endMs !== null && endMs > toMs) return false;
            if (endMs === null && startMs !== null && startMs > toMs) return false;
          }
        }

        return true;
      });
    },
    dailyUploadSeries() {
      const counter = {};
      this.filteredCampaigns.forEach(campaign => {
        const uploads = Array.isArray(campaign.uploadDates) ? campaign.uploadDates : [];
        uploads.forEach(uploadedAt => {
          const date = new Date(uploadedAt);
          if (Number.isNaN(date.getTime())) return;
          const key = date.toISOString().slice(0, 10);
          counter[key] = (counter[key] || 0) + 1;
        });
      });
      const labels = Object.keys(counter).sort();
      const data = labels.map(label => counter[label]);
      return { labels, data };
    },
    workLogDetails() {
      if (!this.selectedCampaign || !this.selectedCampaign.shortDetails) return {};
      return this.selectedCampaign.shortDetails;
    },
    workLogs() {
      if (!this.selectedCampaign || !Array.isArray(this.selectedCampaign.workLog)) return [];
      return this.selectedCampaign.workLog;
    },
    contentUploads() {
      if (!this.contentModalCampaign) return [];
      return Array.isArray(this.contentModalCampaign.channelUploads) ? this.contentModalCampaign.channelUploads : [];
    },
    contentTotalPages() {
      return Math.ceil(this.contentUploads.length / this.contentPerPage) || 1;
    },
    pagedContentUploads() {
      const start = (this.contentPage - 1) * this.contentPerPage;
      return this.contentUploads.slice(start, start + this.contentPerPage);
    },
    joinedCategories() {
      const categories = this.workLogDetails.contentCategories;
      if (!Array.isArray(categories) || !categories.length) return '-';
      return categories.join(', ');
    },
  },
  watch: {
    filteredCampaigns() {
      this.renderChart();
    },
  },
  mounted() {
    this.loadDashboard();
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  },
  methods: {
    async loadDashboard() {
      this.loading = true;
      this.message = '';
      try {
        const response = await sponsorService.getCampaignDashboardData();
        this.campaigns = response.items || [];
        this.summary = response.summary || {
          totalCampaigns: this.campaigns.length,
          runningCampaigns: this.campaigns.filter(item => !item.isArchived).length,
          archivedCampaigns: this.campaigns.filter(item => item.isArchived).length,
        };
        this.$nextTick(() => this.renderChart());
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to load campaign dashboard';
      } finally {
        this.loading = false;
      }
    },
    resetFilters() {
      this.filters = { name: '', status: '', archiveType: 'all', startDate: '', endDate: '' };
    },
    openWorkLog(campaign) {
      this.selectedCampaign = campaign;
    },
    closeWorkLog() {
      this.selectedCampaign = null;
    },
    openContentModal(campaign) {
      this.contentModalCampaign = campaign;
      this.contentPage = 1;
    },
    closeContentModal() {
      this.contentModalCampaign = null;
    },
    contentPrevPage() {
      if (this.contentPage > 1) this.contentPage--;
    },
    contentNextPage() {
      if (this.contentPage < this.contentTotalPages) this.contentPage++;
    },
    actorLabel(actor) {
      if (!actor || typeof actor !== 'object') return 'System';
      return actor.label || actor.username || 'System';
    },
    workLogDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      const day = date.toLocaleDateString(undefined, { day: '2-digit' });
      const month = date.toLocaleDateString(undefined, { month: 'long' });
      const year = date.toLocaleDateString(undefined, { year: 'numeric' });
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const period = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      const hourValue = String(hours).padStart(2, '0');
      return `${day} ${month} ${year} ${hourValue}.${minutes} ${period}`;
    },
    parseDateMs(value, assumeLocalDate = false, endOfDay = false) {
      if (!value) return null;
      if (assumeLocalDate && typeof value === 'string') {
        const parts = value.split('-').map(Number);
        if (parts.length === 3 && parts.every(num => Number.isFinite(num))) {
          const [year, month, day] = parts;
          const date = endOfDay ? new Date(year, month - 1, day, 23, 59, 59, 999) : new Date(year, month - 1, day, 0, 0, 0, 0);
          const time = date.getTime();
          return Number.isNaN(time) ? null : time;
        }
      }
      const date = new Date(value);
      const time = date.getTime();
      return Number.isNaN(time) ? null : time;
    },
    renderChart() {
      const canvas = this.$refs.uploadTrendChart;
      if (!canvas) return;
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }

      const series = this.dailyUploadSeries;
      const labels = series.labels.length ? series.labels : ['No Data'];
      const data = series.data.length ? series.data : [0];

      this.chartInstance = new Chart(canvas, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Daily Sponsor Uploads',
            data,
            fill: true,
            borderColor: '#00796b',
            backgroundColor: 'rgba(0, 121, 107, 0.12)',
            pointBackgroundColor: '#00796b',
            pointRadius: 3,
            pointHoverRadius: 5,
            borderWidth: 2,
            tension: 0.32,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: true, labels: { fontColor: '#2a3e4b' } },
          scales: {
            xAxes: [{ ticks: { fontColor: '#4d6472' }, gridLines: { color: 'rgba(71, 99, 122, 0.12)' } }],
            yAxes: [{ ticks: { fontColor: '#4d6472', beginAtZero: true, precision: 0 }, gridLines: { color: 'rgba(71, 99, 122, 0.12)' } }],
          },
        },
      });
    },
    humanDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    statusLabel(status) {
      return campaignStatusLabel(status);
    },
    riskLabel(level) {
      const map = { critical: 'Critical', at_risk: 'At Risk', warning: 'Warning', on_track: 'On Track', none: 'N/A' };
      return map[level] || 'N/A';
    },
    riskTitle(campaign) {
      const risk = campaign.deadlineRisk;
      if (!risk || !risk.reasons || !risk.reasons.length) return '';
      const parts = [risk.reasons[0]];
      if (risk.daysLeft !== null && risk.daysLeft !== undefined) {
        parts.push(`Days left: ${risk.daysLeft}`);
      }
      return parts.join(' | ');
    },
    riskRowClass(campaign) {
      const level = campaign.deadlineRisk && campaign.deadlineRisk.level;
      if (level === 'critical') return 'row-critical';
      return '';
    },
  },
};
</script>

<style scoped>
.campaign-dashboard-page { --surface: #f2f5f8; --panel: #ffffff; --border: #d8e1e8; --text-main: #1f2d3a; --text-muted: #607382; --accent-soft: #e6f4f2; display: flex; min-height: 100vh; background: radial-gradient(circle at 12% 10%, #e6f4f2 0%, transparent 35%), radial-gradient(circle at 90% 18%, #fff6e3 0%, transparent 34%), var(--surface); }
.content { flex: 1; margin-left: 250px; padding: 24px; }
.page-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; margin-bottom: 14px; }
.page-header h1 { margin: 0; color: var(--text-main); }
.page-header p { margin: 6px 0 0; color: var(--text-muted); }
.btn-refresh { border: 1px solid #0e8e7f; background: linear-gradient(135deg, #12a594 0%, #0e8e7f 100%); color: #fff; border-radius: 10px; padding: 8px 12px; font-size: 12px; font-weight: 700; cursor: pointer; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-bottom: 12px; }
.card { background: var(--panel); border: 1px solid var(--border); border-radius: 14px; padding: 14px; box-shadow: 0 12px 24px rgba(14, 22, 35, 0.06); }
.stat-card .label { margin: 0; color: var(--text-muted); font-size: 12px; font-weight: 700; }
.stat-card strong { display: block; margin-top: 5px; color: var(--text-main); font-size: 28px; }
.stat-card.running { background: linear-gradient(135deg, #ecfdf9 0%, #ffffff 100%); border-color: #9ad7cb; }
.progress-card { background: linear-gradient(135deg, #fff6e9 0%, #ffffff 100%); border-color: #f0d39d; }
.filter-card h2, .chart-card h2, .table-card h2 { margin: 0 0 10px; color: var(--text-main); font-size: 18px; }
.filters { display: grid; grid-template-columns: minmax(220px, 1.5fr) minmax(170px, 1fr) minmax(170px, 1fr) minmax(150px, 1fr) minmax(150px, 1fr) auto; gap: 10px; align-items: end; }
.field { display: flex; flex-direction: column; gap: 4px; }
.field label { color: var(--text-muted); font-size: 12px; font-weight: 700; }
.field input, .field select { border: 1px solid #c9d6df; border-radius: 8px; padding: 8px 10px; font-size: 12px; color: #2b3f4f; background: #fff; }
.actions { display: flex; justify-content: flex-end; }
.btn-clear { border: 1px solid #95a8b6; background: #f6f9fb; color: #334a59; border-radius: 8px; padding: 8px 12px; font-size: 12px; font-weight: 700; cursor: pointer; }
.chart-card, .table-card { margin-top: 12px; }
.section-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.chip { background: var(--accent-soft); color: #14584e; border: 1px solid #b2ddd7; border-radius: 999px; padding: 4px 10px; font-size: 11px; font-weight: 700; }
.chart-wrap { height: 280px; }
.table-wrap { overflow-x: auto; }
.campaign-table { width: 100%; min-width: 1180px; border-collapse: collapse; }
.campaign-table th, .campaign-table td { border-bottom: 1px solid #e2eaf0; text-align: left; padding: 10px 8px; color: #304655; font-size: 13px; }
.campaign-table th { color: #1c3444; background: #f5f9fc; font-weight: 700; }
.status { display: inline-block; border-radius: 999px; padding: 4px 8px; font-size: 11px; font-weight: 700; }
.status.s-campaign_initiated { background: #ecf2ff; color: #2f5fb0; }
.status.s-task_accepted { background: #e8f7ef; color: #18794e; }
.status.s-creative_production { background: #fff2e1; color: #9b5d04; }
.status.s-creative_approved { background: #f0eaff; color: #6238b4; }
.status.s-publishing { background: #e0f2fe; color: #075985; }
.status.s-creative_submission { background: #fdf4ff; color: #7c3aed; }
.status.s-completed { background: #dcfce7; color: #166534; }
.type-pill { display: inline-block; border-radius: 999px; padding: 4px 8px; font-size: 11px; font-weight: 700; }
.type-pill.running { background: #e7f6f3; color: #126356; }
.type-pill.archived { background: #f3f5f8; color: #556b7a; }
.progress-pill { display: inline-block; border-radius: 999px; padding: 4px 8px; font-size: 11px; font-weight: 700; background: #edf8f6; color: #156a5c; border: 1px solid #b8e0d8; }
.empty-state { padding: 20px; text-align: center; color: #607482; font-size: 13px; }
.btn-work-log { border: 1px solid #138675; background: #e8f7f5; color: #0f6b5d; border-radius: 8px; padding: 6px 10px; font-size: 11px; font-weight: 700; cursor: pointer; }
.btn-campaign-name { background: none; border: none; color: #0e6ea1; font-size: 13px; font-weight: 700; cursor: pointer; padding: 0; text-decoration: underline; text-align: left; }
.btn-campaign-name:hover { color: #084e73; }
.content-modal { width: min(1000px, 100%); max-height: calc(100vh - 80px); overflow: auto; }
.content-table { width: 100%; border-collapse: collapse; margin-top: 14px; font-size: 13px; }
.content-table th, .content-table td { border-bottom: 1px solid #dce8ef; padding: 9px 8px; text-align: left; color: #2b3f4f; }
.content-table th { background: #f5f9fc; font-weight: 700; color: #1c3444; }
.content-link { color: #0e6ea1; text-decoration: underline; font-weight: 600; }
.content-link:hover { color: #084e73; }
.content-pagination { display: flex; justify-content: space-between; align-items: center; margin-top: 12px; padding-top: 10px; border-top: 1px solid #e4edf3; }
.pagination-info { color: #607382; font-size: 12px; }
.pagination-controls { display: flex; gap: 8px; }
.btn-page { border: 1px solid #95a8b6; background: #f6f9fb; color: #334a59; border-radius: 8px; padding: 6px 12px; font-size: 12px; font-weight: 700; cursor: pointer; }
.btn-page:disabled { opacity: 0.45; cursor: default; }
.archive-note { color: #6e828f; font-size: 11px; font-weight: 700; }
.modal-backdrop { position: fixed; inset: 0; background: rgba(13, 29, 43, 0.44); display: flex; justify-content: center; align-items: flex-start; padding: 40px 12px; z-index: 999; }
.worklog-modal { width: min(860px, 100%); max-height: calc(100vh - 80px); overflow: auto; }
.modal-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; }
.modal-head h3 { margin: 0; color: #1d3342; }
.btn-close { border: 1px solid #95a8b6; background: #f6f9fb; color: #334a59; border-radius: 8px; padding: 7px 12px; font-size: 12px; font-weight: 700; cursor: pointer; }
.modal-subtitle { margin-top: 8px; color: #5e7483; font-size: 13px; }
.short-details { margin-top: 14px; padding-top: 10px; border-top: 1px solid #e4edf3; }
.short-details h4, .work-log-list h4 { margin: 0 0 10px; color: #213a4b; font-size: 15px; }
.details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 12px; }
.details-grid p { margin: 0; color: #2f495a; font-size: 13px; }
.work-log-list { margin-top: 16px; padding-top: 10px; border-top: 1px solid #e4edf3; }
.work-log-list ul { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 10px; }
.work-log-list li { border: 1px solid #d8e6ee; background: #f8fcff; border-radius: 10px; padding: 10px; display: flex; flex-direction: column; gap: 4px; }
.log-time { color: #1a4f72; font-size: 12px; font-weight: 700; }
.log-text { color: #253f50; font-size: 13px; }
.log-note { color: #5e7382; font-size: 12px; }
/* --- Alert Panel --- */
.alert-panel { margin-bottom: 12px; }
.alert-panel-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.alert-panel-head h2 { margin: 0; color: var(--text-main); font-size: 18px; }
.alert-counts { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin-bottom: 14px; }
.alert-card { display: flex; align-items: center; gap: 12px; border-radius: 12px; padding: 12px 16px; border: 1px solid transparent; }
.alert-card strong { display: block; font-size: 24px; color: inherit; }
.alert-card p { margin: 0; font-size: 12px; font-weight: 700; color: inherit; opacity: 0.85; }
.alert-icon { font-size: 22px; line-height: 1; }
.alert-critical { background: #fff1f0; border-color: #ffc9c9; color: #a8200b; }
.alert-at-risk { background: #fff6ed; border-color: #ffd8a8; color: #883f00; }
.alert-warning { background: #fffbe0; border-color: #ffe168; color: #6e5300; }
.alert-on-track { background: #f0faf6; border-color: #99dec5; color: #136b46; }
/* --- Alert Legend --- */
.alert-legend { background: #f8fafb; border: 1px solid #dce8ef; border-radius: 10px; padding: 12px 16px; }
.legend-title { margin: 0 0 8px; font-size: 12px; font-weight: 700; color: #3a5567; text-transform: uppercase; letter-spacing: 0.04em; }
.legend-list { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 6px; }
.legend-list li { font-size: 12px; color: #3d5567; display: flex; align-items: flex-start; gap: 8px; }
/* --- Risk Badge (shared) --- */
.risk-badge { display: inline-block; border-radius: 999px; padding: 3px 9px; font-size: 11px; font-weight: 700; white-space: nowrap; }
.risk-critical { background: #fff1f0; color: #a8200b; border: 1px solid #ffc9c9; }
.risk-at_risk { background: #fff6ed; color: #883f00; border: 1px solid #ffd8a8; }
.risk-warning { background: #fffbe0; color: #6e5300; border: 1px solid #ffe168; }
.risk-on_track { background: #f0faf6; color: #136b46; border: 1px solid #99dec5; }
.risk-none { background: #f3f5f8; color: #556b7a; border: 1px solid #d4dde5; }
/* --- Critical row highlight --- */
.row-critical td { background: rgba(255, 230, 228, 0.25); }
@media (max-width: 1024px) { .content { margin-left: 200px; } .summary-grid { grid-template-columns: 1fr 1fr; } .filters { grid-template-columns: 1fr 1fr; } .alert-counts { grid-template-columns: 1fr 1fr; } }
@media (max-width: 768px) { .content { margin-left: 0; padding: 14px; } .page-header { flex-direction: column; align-items: stretch; } .summary-grid { grid-template-columns: 1fr; } .filters { grid-template-columns: 1fr; } .actions { justify-content: flex-start; } .chart-wrap { height: 220px; } .details-grid { grid-template-columns: 1fr; } .alert-counts { grid-template-columns: 1fr 1fr; } }
</style>
